<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {injectScoreEditContext} from '@/components/musicxml-preview/editContext.ts';
import TimeSignaturePicker from '@/components/time-signature/TimeSignaturePicker.vue';

// Selektions-State + Edit-Aktion aus dem von useOsmdInteractiveScore bereitgestellten Kontext
// (analog KeySignatureEditView) – kein Prop-Drilling/Emit mehr.
const {
  currentTimeSignature,
  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  saveTimeSignature,
  checkTimeSignatureOverfull,
  canRebar,
  rebarBlockedReason,
  rebarFromMeasure,
} = injectScoreEditContext();


const selectedBeats = ref(4);
const selectedBeatType = ref(4);

// Zwei Modi, beide erst auf Klick. „Noten neu aufteilen" (Default, wo angeboten): der Server setzt
// die Taktart ab diesem Takt bis Stückende und verteilt die Noten neu. Sonst ändert sich NUR dieser
// Takt; sein Inhalt wird aufgefüllt bzw. am Ende gekürzt (`setMeasureMeterInMusicXml`). Bewusst kein
// Auto-Save: der Picker-Weg 4/4 → 2/4 → 2/2 löschte sonst Noten und füllte danach nur Pausen auf.
const rebarMode = ref(true);
const isRebarMode = computed(() => canRebar.value && rebarMode.value);

// Ein-Takt-Modus: ist der Inhalt länger als das gewählte Metrum, fallen Noten am Taktende weg –
// vorab als Hinweis zeigen (keine Sperre).
const isOverfull = computed(() =>
    measureNumber.value !== null
    && checkTimeSignatureOverfull(
        measureNumber.value, selectedBeats.value, selectedBeatType.value, measureIndex.value ?? undefined,
    ),
);

const isUnchanged = computed(() =>
    selectedBeats.value === (currentTimeSignature.value?.beats ?? 4)
    && selectedBeatType.value === (currentTimeSignature.value?.beatType ?? 4),
);

// Vorhandene Taktart übernehmen, sobald sich Takt/Taktart ändert.
watch(() => currentTimeSignature.value, (timeSignature) => {
  if (timeSignature) {
    selectedBeats.value = timeSignature.beats;
    selectedBeatType.value = timeSignature.beatType;
  } else {
    selectedBeats.value = 4;
    selectedBeatType.value = 4;
  }
}, {immediate: true});

function handleSave() {
  if (isRebarMode.value || measureNumber.value === null) return;
  saveTimeSignature({
    measureNumber: measureNumber.value,
    measureIndex: measureIndex.value ?? undefined,
    beats: selectedBeats.value,
    beatType: selectedBeatType.value,
  });
}

function handleRebar() {
  rebarFromMeasure(selectedBeats.value, selectedBeatType.value);
}
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="measureNumber !== null"
        class="flex items-center justify-center p-2 overflow-auto"
    >
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

        <!-- Header -->
        <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-cyan-50">
          <div>
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-3">
              <div class="p-2 bg-sky-100 text-sky-600 rounded-lg">
                <Icon icon="fa6-solid:clock" class="text-lg"/>
              </div>
              Taktart bearbeiten
            </h2>
            <p class="text-sm text-gray-600 mt-1">{{ isRebarMode ? 'Ab Takt' : 'Takt' }} {{ measureNumber }}</p>
          </div>
        </div>

        <!-- Content -->
        <div class="p-5 space-y-5">
          <TimeSignaturePicker v-model:beats="selectedBeats" v-model:beat-type="selectedBeatType"/>

          <label v-if="canRebar" class="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" v-model="rebarMode" class="mt-0.5 accent-sky-500"/>
            <span>
              <strong>Noten neu aufteilen</strong>
              <span class="block text-xs text-gray-500">
                Ab diesem Takt bis zum Ende: Takte neu bilden, überhängende Noten binden.
                Aus = nur diesen Takt umstellen (Inhalt wird aufgefüllt bzw. am Ende gekürzt).
              </span>
            </span>
          </label>

          <div v-if="isRebarMode" class="flex flex-col gap-2">
            <button
                type="button"
                class="flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!!rebarBlockedReason || isUnchanged"
                :title="rebarBlockedReason || undefined"
                @click="handleRebar"
            >
              <Icon icon="fa6-solid:scissors"/>
              Ab Takt {{ measureNumber }} anwenden
            </button>
            <p v-if="rebarBlockedReason" class="text-xs text-amber-700">{{ rebarBlockedReason }}</p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <!-- Hinweis: Metrum kürzer als der Takt-Inhalt → Noten am Taktende fallen weg. -->
            <div
                v-if="isOverfull"
                class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
            >
              <Icon icon="fa6-solid:triangle-exclamation" class="mt-0.5 shrink-0"/>
              <span>
                Das gewählte Metrum ist kürzer als der Takt-Inhalt – Noten am Taktende werden
                <strong>gelöscht</strong>.
                <template v-if="canRebar">Mit „Noten neu aufteilen" bleiben alle Noten erhalten.</template>
              </span>
            </div>
            <button
                type="button"
                class="flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isUnchanged"
                @click="handleSave"
            >
              <Icon icon="fa6-solid:check"/>
              Auf Takt {{ measureNumber }} anwenden
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
